import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Button, type ButtonProps } from "@/components/ui/button";
import type { ChoiceModalButton, ChoiceModalDescriptor } from "@/services/simple-modal/simple-modal-types";

export interface ChoiceSimpleModalProps {
    modal: ChoiceModalDescriptor;
    // Internal callbacks to handle actions and resolve state
    onSelect: (value: string) => void;
    onError?: (reason: unknown) => void;
}

const buttonVariant = (tone: ChoiceModalButton["tone"], isPrimary: boolean): ButtonProps["variant"] => {
    if (tone === "danger") {
        return "destructive";
    }
    if (tone === "warning") {
        return "destructive-outline";
    }
    // Only the primary slot takes the filled style; every other button stays neutral.
    return isPrimary ? "default" : "outline";
};

export function ChoiceSimpleModal({ modal, onSelect, onError }: ChoiceSimpleModalProps) {
    const [pendingValue, setPendingValue] = useState<string>();
    const isLoading = pendingValue !== undefined;

    const handleClick = async (button: ChoiceModalButton) => {
        if (!button.onSelect) {
            onSelect(button.value);
            return;
        }
        setPendingValue(button.value);
        try {
            await button.onSelect();
            // onSelect() closes (unmounts) the modal, so there's no need to reset the pending value here
            onSelect(button.value);
        } catch (error) {
            setPendingValue(undefined);
            onError?.(error);
        }
    };

    return (
        <>
            {modal.body ? <div>{modal.body}</div> : null}
            <div className={"flex flex-wrap justify-end gap-4 max-sm:flex-col max-sm:gap-4"}>
                {modal.buttons.map((button, index) => (
                    <Button
                        key={button.value}
                        size={"lg"}
                        disabled={isLoading}
                        onClick={() => handleClick(button)}
                        type={"button"}
                        variant={buttonVariant(button.tone, index === modal.buttons.length - 1)}
                    >
                        {pendingValue === button.value ? <Loader2 className={"animate-spin"} /> : button.label}
                    </Button>
                ))}
            </div>
        </>
    );
}
